import { useEffect, useId, useRef, useState, type ReactElement, type ReactNode } from "react";
import { CentredRow } from "./Centred.tsx";
import { focused, tabbable } from "./focus.ts";
import { Icon, type IconName } from "./Icon.tsx";
import { KIT_METRICS, logical } from "./metrics.ts";

/**
 * A persistent notice (brief section 6.3): what happened, with no timer, for a disconnect, a failed load or
 * a moderator's action. It stays until the player acts on it: an icon, a title in the pixel face, one
 * sentence in running text, a detail line beneath it for the specifics (a close code and its reason, a failed
 * step) set as a chip as wide as its text, and the actions, usually buttons, all centred in one column as the
 * design draws it (ADR-061's amendment "the kit and the entry forms match the design"; the icon is the free
 * set's at 24, where the design draws 16). It is drawn in the dialog frame and takes the pointer. The words
 * come from the surface that shows it; the notice writes none of its own.
 *
 * With actions it asks for a response, so it is an `alertdialog` (the ARIA pattern for an alert with
 * actions), named by its title and described by its sentence and detail line, and it takes focus to its
 * first action as it appears, which is how assistive technology reads it; as it leaves the page it gives
 * focus back to what had it as it appeared, unless focus has moved on. Its Now consumers all ask for a
 * response: P1.9-T15's disconnect notices (Reconnect, Reload or Log in), P1.4-T5's load failures (Retry) and
 * P1.4-T8's moderation notices (acknowledging a warning). It is not modal: it neither traps Tab nor holds
 * the keyboard, so it sets no `aria-modal`, and a surface that must keep keys from the game behind it
 * holds the input router itself. With no actions (a ban with no way back, a freeze) it is an `alert`, read
 * as it appears, and takes no focus.
 */

/**
 * A persistent notice.
 *
 * @param props The `icon`, the alert unless given, which is decoration, since the title says what happened;
 *   the `title`; the `sentence`; the `detail` line, when there is one; the `actions`, when the notice asks for
 *   a response; and the notice's `width` in logical pixels.
 * @returns The notice.
 * @example
 * <PersistentNotice title={title} sentence={sentence} detail={`closed: ${code} ${reason}`} actions={<Button variant="primary" onClick={reload}>{label}</Button>} width={220} />
 */
export function PersistentNotice({ icon = "alert", title, sentence, detail, actions, width }: { readonly icon?: IconName; readonly title: string; readonly sentence: string; readonly detail?: string; readonly actions?: ReactNode; readonly width: number }): ReactElement {
  const titleId = useId();
  const sentenceId = useId();
  const detailId = useId();
  const row = useRef<HTMLElement | null>(null);
  const [opener] = useState(focused);
  const asks = actions !== undefined && actions !== null && actions !== false;
  const metrics = KIT_METRICS.notice;

  useEffect(() => {
    if (!asks) {
      return undefined;
    }
    const first = row.current === null ? undefined : tabbable(row.current)[0];
    first?.focus();
    return () => {
      const now = document.activeElement;
      if ((now === null || now === document.body) && opener instanceof HTMLElement && opener.isConnected) {
        opener.focus();
      }
    };
  }, [asks, opener]);

  return (
    <section
      className="ot-notice ot-ui-frame-dialog"
      role={asks ? "alertdialog" : "alert"}
      aria-labelledby={titleId}
      aria-describedby={asks ? (detail === undefined ? sentenceId : `${sentenceId} ${detailId}`) : undefined}
      data-kit="notice"
      style={{ width: logical(width), padding: logical(metrics.padding), gap: logical(metrics.gap) }}
    >
      <CentredRow>
        <Icon name={icon} role="slot" />
      </CentredRow>
      <h2 id={titleId} className="ot-notice__title" data-kit="notice-title">
        {title}
      </h2>
      <p id={sentenceId} className="ot-notice__sentence" data-kit="notice-sentence">
        {sentence}
      </p>
      {detail === undefined ? null : (
        <CentredRow>
          <p id={detailId} className="ot-notice__detail ot-centred__text" data-kit="notice-detail" style={{ padding: `${logical(metrics.detailPaddingY)} ${logical(metrics.detailPaddingX)}` }}>
            {detail}
          </p>
        </CentredRow>
      )}
      {asks ? (
        <CentredRow rowRef={row} className="ot-notice__actions" kit="notice-actions" style={{ gap: logical(metrics.actionsGap) }}>
          {actions}
        </CentredRow>
      ) : null}
    </section>
  );
}
